import { match, P } from 'ts-pattern'

import { Nullable, IsNull } from 'src/types/utils'
import { breakpoints, breakpointsRaw } from 'src/theme/adSales/gel'

const LOW_RES_DIVISION = 4

const ratios = {
  '16x9': (w: number) => ({ width: w, height: Math.round((w / 16) * 9) }),
  '3x4': (w: number) => ({ width: w, height: Math.round((w / 3) * 4) }),
  '2x3': (w: number) => ({ width: w, height: Math.round((w / 2) * 3) }),
  '1x1': (w: number) => ({ width: w, height: w })
}
type ImageRatio = keyof typeof ratios

const imageVariants = {
  '16x9-fullwidth': {
    srcSize: 1280,
    setSizes: [
      ...breakpointsRaw,
      ...breakpointsRaw.map((val: number) => (val * 2 > 2000 ? 2000 : val * 2))
    ],
    sizes: '100vw',
    ratio: <ImageRatio>'16x9',
    addLowRes: true
  },
  '16x9-carousel-card': {
    srcSize: 299,
    setSizes: [299, 299 * 2],
    sizes: '100vw',
    ratio: <ImageRatio>'16x9',
    addLowRes: true
  },
  '16x9-list-card': {
    srcSize: 600,
    setSizes: [600, 404, 600 * 2, 404 * 2],
    sizes: `(min-width: ${breakpoints[1]}) 404px, calc(100vw - 1em)`,
    ratio: <ImageRatio>'16x9',
    addLowRes: true
  },
  '1x1-hero-logo': {
    srcSize: 575,
    setSizes: [266, 266 * 2, 575, 575 * 2],
    sizes: `(min-width: ${breakpoints[1]}) 575px, calc(100vw - 1em)`,
    ratio: <ImageRatio>'1x1',
    addLowRes: true
  }
}

const placeholderRoute = '/images/placeholders/'
const placeholders = {
  dark: { src: `${placeholderRoute}dark.jpg` },
  midDark: { src: `${placeholderRoute}midDark.jpg` },
  light: { src: `${placeholderRoute}light.jpg` },
  medium: { src: `${placeholderRoute}medium.jpg` }
}
const getPlaceholder = (fallback?: ImagePlaceholder) => placeholders[fallback ?? 'midDark']

type ImageAssetVariant = keyof typeof imageVariants
type ImagePlaceholder = keyof typeof placeholders

type ImageAsset = {
  src: string
  alttext?: string
  srcSet?: string
  lowSrc?: string
  lowSrcSet?: string
  sizes?: string
  fallback?: boolean
}

type ImageAssetProps = Partial<{
  variant: ImageAssetVariant
  src: string | null
  altText: string
  width: number
  height: number
  defaultSize: number
  setSizes: number[]
  sizes: string
  ratio: ImageRatio
  addLowRes: boolean
  placeholderImage: ImagePlaceholder
}>

type AssetParams = Partial<{
  width: number
  height: number
  download: string
}>

const imageRatio = (ratio: ImageRatio) => ratios[ratio]

const getImageVariant = (path: string, variant: ImageAssetVariant) =>
  getImageAssetSet(
    path,
    imageVariants[variant].srcSize,
    imageVariants[variant].setSizes,
    imageVariants[variant].sizes,
    imageVariants[variant].ratio
  )

const getImageAsset = (input: ImageAssetProps): ImageAsset | null =>
  match(input)
    .with(
      {
        src: P.string,
        defaultSize: P.number,
        altText: P.optional(P.string),
        setSizes: P.array(P.number),
        sizes: P.optional(P.string),
        ratio: P.optional(P.string),
        addLowRes: P.optional(P.boolean)
      },
      ({ src, defaultSize, setSizes, sizes, ratio, addLowRes }) =>
        <any>(
          getImageAssetSet(src, defaultSize, setSizes, sizes || '101vw', ratio || '16x9', addLowRes)
        )
    )
    .with(
      {
        src: P.string,
        width: P.optional(P.number),
        height: P.optional(P.number),
        variant: P.optional(P.nullish)
      },
      ({ src, width, height }) => {
        const asset = getAsset(src, {
          width,
          height
        })
        return {
          src: asset
        }
      }
    )
    .with(
      { src: P.string, variant: P.not(P.nullish) },
      ({ variant, src }) => <any>getImageVariant(src, variant)
    )
    .with({ placeholderImage: P.not(P.nullish) }, ({ placeholderImage }) =>
      getPlaceholder(placeholderImage)
    )
    .otherwise(() => getPlaceholder())

const getImageAssetSet = (
  path: null | string,
  defaultSize = 600,
  setSizes = [600, 600 * 2],
  sizes = '100vw',
  ratio: ImageRatio = '16x9',
  addLowRes = true
): {
  sizes: string
  src: string
  srcSet: string
  lowSrc: string | null
  lowSrcSet: string | null
} => {
  if (!path) return <any>{ src: '' }
  return {
    src: getAsset(path, imageRatio(ratio)(defaultSize)),
    srcSet: setSizes
      .map(imageRatio(ratio))
      .map(asset => `${getAsset(path, asset)} ${asset.width}w`)
      .join(', '),
    sizes,
    lowSrc: addLowRes
      ? getAsset(path, imageRatio(ratio)(Math.round(defaultSize / LOW_RES_DIVISION)))
      : null,
    lowSrcSet: addLowRes
      ? setSizes
          .map(imageRatio(ratio))
          .map(
            asset =>
              `${getAsset(path, {
                width: Math.round(asset.width / LOW_RES_DIVISION),
                height: Math.round(asset.height / LOW_RES_DIVISION)
              })} ${asset.width}w`
          )
          .join(', ')
      : null
  }
}

const getAsset = <T extends Nullable<string>>(path: T, params?: AssetParams): IsNull<T> => {
  if (path) {
    return <any>processAsset(path, {
      ...params
    })
  }
  return <any>null
}

const processAsset = (path: string, assetParams?: AssetParams) =>
  assetParams
    ? `${path}?${Object.entries(assetParams)
        .map(e => `${e[0]}=${e[1]}`)
        .join('&')}`
    : path

export { getAsset, getImageAsset }
